/* Walkthrough step definitions for AI-driven guided walkthroughs.
   Each walkthrough is a sequence of steps that highlight UI elements and guide the user. */

import { RoutePath } from "@/routePaths";

export interface WalkthroughStep {
  targetSelector: string;
  route?: string;
  title: string;
  description: string;
  placement: "top" | "bottom" | "left" | "right";
  autoAdvanceOnClick?: boolean;
  waitForSelector?: string;
  delayMs?: number;
}

export interface GuidedWalkthrough {
  id: string;
  name: string;
  description: string;
  steps: WalkthroughStep[];
}

export const GUIDED_WALKTHROUGHS: GuidedWalkthrough[] = [
  {
    id: "add-source",
    name: "Add a new source",
    description: "Walk through adding a new content source",
    steps: [
      {
        targetSelector: "[data-guide='nav-sources']",
        route: RoutePath.Sources,
        title: "Go to Sources",
        description: "Click Sources in the sidebar to manage your content sources.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='add-source-button']",
        route: RoutePath.Sources,
        title: "Add a source",
        description: "Click the + button to add a new content source.",
        placement: "bottom",
        autoAdvanceOnClick: true,
      },
    ],
  },
  {
    id: "update-feed",
    name: "Update your feed",
    description: "Walk through refreshing your feed with latest content",
    steps: [
      {
        targetSelector: "[data-guide='nav-feed']",
        route: RoutePath.Feed,
        title: "Go to your Feed",
        description: "Click Feed in the sidebar to see your latest content.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='update-feed-button']",
        route: RoutePath.Feed,
        title: "Update your feed",
        description: "Click the update button to fetch the latest content from all your sources.",
        placement: "bottom",
        autoAdvanceOnClick: true,
      },
    ],
  },
  {
    id: "queue-item",
    name: "Queue a feed item",
    description: "Walk through adding a feed item to your queue",
    steps: [
      {
        targetSelector: "[data-guide='nav-feed']",
        route: RoutePath.Feed,
        title: "Go to your Feed",
        description: "Click Feed in the sidebar to browse your content.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='feed-item-actions']",
        route: RoutePath.Feed,
        title: "Queue an item",
        description: "Hover over a feed item and click the queue button to save it for later.",
        placement: "left",
        autoAdvanceOnClick: true,
        delayMs: 500,
      },
    ],
  },
  {
    id: "shelve-item",
    name: "Save an item to backlog",
    description: "Walk through saving a feed item to your backlog",
    steps: [
      {
        targetSelector: "[data-guide='nav-feed']",
        route: RoutePath.Feed,
        title: "Go to your Feed",
        description: "Click Feed in the sidebar to browse your content.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='feed-item-actions']",
        route: RoutePath.Feed,
        title: "Save to backlog",
        description: "Hover over a feed item and click the backlog button to save it for later.",
        placement: "left",
        autoAdvanceOnClick: true,
        delayMs: 500,
      },
    ],
  },
  {
    id: "create-tag-goal",
    name: "Create a daily goal",
    description: "Walk through creating a daily tag-based consumption goal",
    steps: [
      {
        targetSelector: "[data-guide='goals-section']",
        route: RoutePath.Feed,
        title: "Find the Goals section",
        description: "Look at the Goals section in the sidebar to see your daily targets.",
        placement: "right",
      },
    ],
  },
  {
    id: "change-theme",
    name: "Change app theme",
    description: "Walk through changing the app's appearance",
    steps: [
      {
        targetSelector: "[data-guide='nav-settings']",
        route: RoutePath.Settings,
        title: "Go to Settings",
        description: "Click Settings in the sidebar to access app configuration.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='theme-section']",
        route: RoutePath.Settings,
        title: "Change the theme",
        description: "Use the Theme section to switch between light, dark, and system modes or change the color scheme.",
        placement: "bottom",
      },
    ],
  },
  {
    id: "change-playback-speed",
    name: "Adjust display settings",
    description: "Walk through changing display and feed item settings",
    steps: [
      {
        targetSelector: "[data-guide='nav-settings']",
        route: RoutePath.Settings,
        title: "Go to Settings",
        description: "Click Settings in the sidebar to access app configuration.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
      {
        targetSelector: "[data-guide='display-section']",
        route: RoutePath.Settings,
        title: "Adjust display settings",
        description: "Change the feed item size, toggle animations, and customize your display preferences.",
        placement: "bottom",
      },
    ],
  },
  {
    id: "view-stats",
    name: "View your stats",
    description: "Walk through viewing your consumption statistics",
    steps: [
      {
        targetSelector: "[data-guide='nav-stats']",
        route: RoutePath.Stats,
        title: "Go to Stats",
        description: "Click Stats in the sidebar to view your consumption statistics and progress.",
        placement: "right",
        autoAdvanceOnClick: true,
      },
    ],
  },
];

/** Look up a walkthrough by its ID. */
export function getWalkthroughById(id: string): GuidedWalkthrough | undefined {
  return GUIDED_WALKTHROUGHS.find((w) => w.id === id);
}

/** Map of walkthrough IDs to names + step counts (for the AI tool). */
export const WALKTHROUGH_SUMMARY: Record<string, { name: string; stepCount: number }> = {};
for (const w of GUIDED_WALKTHROUGHS) {
  WALKTHROUGH_SUMMARY[w.id] = { name: w.name, stepCount: w.steps.length };
}
